<template>
    <!-- Empty tag to make ssr rehydration happy — it complains if the template is empty -->
    <div data-head-meta-game />
</template>
<script setup lang="ts">
import { type ReactiveHead, useHead } from '@unhead/vue'
import { computed, toRef } from 'vue'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import { useConfig } from '@/composables/config'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { SCHEMA_BEST_RATING, SCHEMA_CURRENCY, SCHEMA_WORST_RATING, useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import { MANIFEST_META_KEY, PLAYGAMA_VIDEO, PWA_APP_CLIDS, TEST_GAMES } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { ManifestGenerator } from '@/modules/pwa'
import {
    getGameDescription,
    getGameTitleWithDeveloperInfo,
    getGameTitleWithUpdateInfo,
    getSubCategories,
} from '@/pages/Games/utils'
import { ROUTE_NAMES } from '@/router/constants'
import { SERVER_QUERY, SSR_QUERY } from '@/router/query-params'
import { usePWAInstallStore } from '@/store/pwa-install/store'
import type { APIGameType, GameCategoriesInfoType } from '@/types'
import safeURLParse from '@/utils/safeURLParse'

const PREVIEW_BIG_WIDTH = 1200
const PREVIEW_BIG_HEIGHT = 675
const PREVIEW_WIDTH = 448
const PREVIEW_HEIGHT = 252
const OG_IMAGE_WIDTH = 1200
const OG_IMAGE_HEIGHT = 630

const props = defineProps<{
    breadcrumbs?: { href: string | RouteLocationRaw; title: string }[]
    game?: APIGameType
    gameCategoriesInfo?: GameCategoriesInfoType
    gameIframeURL?: string
}>()

const game = toRef(() => props.game)
const gameCategoriesInfo = toRef(() => props.gameCategoriesInfo)

const tr = useTr()
const router = useRouter()
const pwaStore = usePWAInstallStore()

const { getCanonicalAndAlternates } = useLocalizedRouters()
const { Organization, Founder, WebSite, WebSiteId, getInLanguage, getFullLink } = useSchemaOrg()
const { currentLocale } = useI18nAddon()
const doNotIndex = computed(() => TEST_GAMES.includes(game.value?.hru || ''))
const isGameTitleInTestGroup = computed(() => game.value?.meta.includes('seo_title_experiments'))
const metaDescription = computed(() => getGameDescription(game.value, tr))
const metaTitleBold = computed(() =>
    isGameTitleInTestGroup.value
        ? getGameTitleWithUpdateInfo(game.value, tr, true, currentLocale.value)
        : getGameTitleWithDeveloperInfo(game.value, tr, true),
)
const metaTitle = computed(() =>
    isGameTitleInTestGroup.value
        ? getGameTitleWithUpdateInfo(game.value, tr, false, currentLocale.value)
        : getGameTitleWithDeveloperInfo(game.value, tr, false),
)

const { VUE_APP_HOST_URL } = useConfig()

const gamePlatforms = computed(() =>
    [
        game.value?.is_desktop && tr('gameContainer.desktop'),
        game.value?.is_android && tr('gameContainer.android'),
        game.value?.is_ios && tr('gameContainer.ios'),
    ]
        .filter(Boolean)
        .join(', '),
)

const schemaOrgBreadcrumb = computed(() => ({
    breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: props.breadcrumbs?.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'Thing',
                '@id': getFullLink(item.href),
                name: item.title,
            },
        })),
    },
}))

const createdAt = computed(() => {
    if (!game.value?.createdAt) {
        return undefined
    }
    const date = new Date(game.value.createdAt)
    date.setDate(date.getDate() - 1)
    return date.toISOString().slice(0, 10)
})

const updatedAt = computed(() => {
    if (!game.value?.updatedAt) {
        return undefined
    }
    const date = new Date(game.value.updatedAt)
    return date.toISOString().slice(0, 10)
})

function getOrganizationSchema() {
    if (!game.value) {
        return {}
    }

    const hasPreviewBig = Boolean(game.value.big_preview)
    const IMAGE_WIDTH = hasPreviewBig ? PREVIEW_BIG_WIDTH : PREVIEW_WIDTH
    const IMAGE_HEIGHT = hasPreviewBig ? PREVIEW_BIG_HEIGHT : PREVIEW_HEIGHT
    const imageUrl = game.value.big_preview || game.value.preview

    const imageCaption = tr('seo.image_caption', { title: game.value.title })
    const imageName = `${game.value.title} Cover Art`
    const inLanguage = getInLanguage()

    const currentRoute = getFullLink({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value.hru } })
    const pageId = `${currentRoute}#item`

    const { title } = game.value || {}

    const availableDevices = []
    if (game.value.is_desktop) {
        availableDevices.push('Desktop')
    }
    if (game.value.is_android || game.value.is_ios) {
        availableDevices.push('Mobile', 'Tablet')
    }

    // Common app properties
    const appBaseProps = {
        '@id': `${currentRoute}#Game`,
        name: title,
        url: currentRoute,
        image: {
            '@type': 'ImageObject',
            url: imageUrl,
            contentUrl: imageUrl,
            width: IMAGE_WIDTH,
            height: IMAGE_HEIGHT,
        },
        applicationSubCategory: gameCategoriesInfo.value
            ? getSubCategories('category', gameCategoriesInfo.value).map((item) => item.title)
            : [],
        genre: gameCategoriesInfo.value ? gameCategoriesInfo.value?.categoriesInfo.map((item) => item.title) : [],
        availableOnDevice: availableDevices,
        operatingSystem: gamePlatforms.value,
        ...(game.value.developer ? { author: { '@type': 'Person', name: game.value.developer } } : {}),
        ...(game.value.rating
            ? {
                  aggregateRating: {
                      '@type': 'AggregateRating',
                      worstRating: SCHEMA_WORST_RATING,
                      bestRating: SCHEMA_BEST_RATING,
                      ratingValue: game.value.rating.value,
                      ratingCount: game.value.rating.count,
                  },
              }
            : {}),
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: SCHEMA_CURRENCY,
            availability: 'https://schema.org/InStock',
        },
        ...(createdAt.value ? { datePublished: createdAt.value } : {}),
        ...(updatedAt.value ? { dateModified: updatedAt.value } : {}),
        potentialAction: {
            '@type': 'PlayAction',
            target: currentRoute,
            name: title,
        },
    }

    // New schema: SoftwareApplication as top-level entity (for test group)
    if (isGameTitleInTestGroup.value) {
        const SoftwareApplication = {
            '@type': 'SoftwareApplication',
            ...appBaseProps,
            applicationCategory: 'GameApplication',
            mainEntityOfPage: { '@id': pageId },
        }

        const itemPage = {
            '@type': 'ItemPage',
            '@id': pageId,
            url: currentRoute,
            name: metaTitleBold.value,
            description: metaDescription.value,
            inLanguage: getInLanguage(),
            ...(props.breadcrumbs ? schemaOrgBreadcrumb.value : {}),
            primaryImageOfPage: {
                '@type': 'ImageObject',
                url: imageUrl,
                contentUrl: imageUrl,
                width: IMAGE_WIDTH,
                height: IMAGE_HEIGHT,
                caption: imageCaption,
                name: imageName,
                representativeOfPage: true,
                inLanguage,
            },
            image: {
                '@type': 'ImageObject',
                url: imageUrl,
                contentUrl: imageUrl,
                width: IMAGE_WIDTH,
                height: IMAGE_HEIGHT,
                caption: imageCaption,
                name: imageName,
                inLanguage,
            },
            isPartOf: {
                '@id': WebSiteId.value,
            },
            mainEntity: {
                '@id': `${currentRoute}#Game`,
            },
        }

        return {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@graph': [Organization.value, Founder.value, WebSite.value, SoftwareApplication, itemPage],
            }),
        }
    }

    // Old schema: VideoGame/WebApplication nested in ItemPage.mainEntity
    const appNode = {
        '@type': ['VideoGame', 'WebApplication'],
        ...appBaseProps,
        applicationCategory: 'Game',
        gamePlatform: [
            'https://schema.org/DesktopWebPlatform',
            'https://schema.org/MobileWebPlatform',
            'https://schema.org/AndroidPlatform',
            'https://schema.org/IOSPlatform',
        ],
        mainEntityOfPage: { '@id': pageId },
    }

    const itemPage = {
        '@type': 'ItemPage',
        '@id': pageId,
        url: currentRoute,
        name: metaTitleBold.value,
        description: metaDescription.value,
        inLanguage,
        ...(props.breadcrumbs ? schemaOrgBreadcrumb.value : {}),
        primaryImageOfPage: {
            '@type': 'ImageObject',
            url: imageUrl,
            contentUrl: imageUrl,
            width: IMAGE_WIDTH,
            height: IMAGE_HEIGHT,
            caption: imageCaption,
            name: imageName,
            representativeOfPage: true,
            inLanguage,
        },
        image: {
            '@type': 'ImageObject',
            url: imageUrl,
            contentUrl: imageUrl,
            width: IMAGE_WIDTH,
            height: IMAGE_HEIGHT,
            caption: imageCaption,
            name: imageName,
            inLanguage,
        },
        isPartOf: {
            '@id': WebSiteId.value,
        },
        mainEntity: appNode,
    }

    return {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [Organization.value, Founder.value, WebSite.value, itemPage],
        }),
    }
}

const pageMeta = computed<ReactiveHead>(() => {
    if (!game.value) {
        return {}
    }

    const altImg = game.value.title
    const hasPreviewBig = Boolean(game.value.big_preview)
    const socialImage = game.value.og_image || game.value.big_preview || game.value.preview
    const coverWidth = hasPreviewBig ? PREVIEW_BIG_WIDTH : PREVIEW_WIDTH
    const coverHeight = hasPreviewBig ? PREVIEW_BIG_HEIGHT : PREVIEW_HEIGHT
    const socialImageWidth = game.value.og_image ? OG_IMAGE_WIDTH : coverWidth
    const socialImageHeight = game.value.og_image ? OG_IMAGE_HEIGHT : coverHeight
    const metaURL = `${VUE_APP_HOST_URL}${
        router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: game.value.hru },
        }).fullPath
    }`
    const pwaStartURL = `${VUE_APP_HOST_URL}${
        router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: game.value.hru },
            query: { [SSR_QUERY.CLID]: PWA_APP_CLIDS.pwa },
        }).fullPath
    }`

    const links: ReactiveHead['link'] = [
        ...getCanonicalAndAlternates({
            forRoute: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value.hru } },
        }),
        {
            rel: 'manifest',
            'data-start_url': pwaStartURL,
            href: `/manifest/game/${game.value.hru}.webmanifest?clid=${PWA_APP_CLIDS.pwa}${
                pwaStore.webAppManifestGame === game.value.hru ? `&${SERVER_QUERY.WEB_APP_MANIFEST}=1` : ''
            }`,
            key: MANIFEST_META_KEY,
        },
        {
            rel: 'apple-touch-icon',
            sizes: '180x180',
            href: ManifestGenerator.getScaledImage(game.value.icon, 180, 180),
            key: 'apple-touch-icon',
        },
    ]

    const scripts: ReactiveHead['script'] = [getOrganizationSchema()]

    if (props.gameIframeURL) {
        const gameIframeOrigin = safeURLParse(props.gameIframeURL)?.origin

        if (gameIframeOrigin && gameIframeOrigin !== safeURLParse(VUE_APP_HOST_URL)?.origin) {
            links.push({ rel: 'preconnect', href: gameIframeOrigin })
        }

        links.push({
            rel: 'preload',
            'data-early-hints': true,
            as: 'image',
            href: game.value.preview,
            fetchpriority: 'high',
        })
    }

    const playgamaVideoId = game.value.video_metadata?.[0]?.playgama_id
    const playgamaVideo = playgamaVideoId ? PLAYGAMA_VIDEO(playgamaVideoId, 640) : null
    const shareVideoURL = game.value.og_video || playgamaVideo?.url

    return {
        title: metaTitleBold.value,
        meta: [
            { name: 'description', content: metaDescription.value },
            { property: 'og:type', content: 'website' },
            { property: 'og:site_name', content: 'Playgama' },
            { property: 'og:title', content: metaTitle.value },
            { property: 'og:description', content: metaDescription.value },
            { property: 'og:url', content: metaURL },
            { property: 'og:image', content: socialImage },
            { property: 'og:image:width', content: `${socialImageWidth}` },
            { property: 'og:image:height', content: `${socialImageHeight}` },
            { property: 'og:image:alt', content: altImg },
            ...(playgamaVideo
                ? [
                      { property: 'og:video', content: shareVideoURL },
                      { property: 'og:video:secure_url', content: shareVideoURL },
                      { property: 'og:video:type', content: 'video/mp4' },
                      { property: 'og:video:width', content: `${playgamaVideo.width}` },
                      { property: 'og:video:height', content: `${playgamaVideo.height}` },
                  ]
                : []),
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: metaTitle.value },
            { name: 'twitter:description', content: metaDescription.value },
            { name: 'twitter:image', content: socialImage },
            { name: 'twitter:image:alt', content: altImg },

            { name: 'apple-mobile-web-app-title', content: game.value.title },
            { property: 'game:platform', content: gamePlatforms.value },
            doNotIndex.value ? { name: 'robots', content: 'noindex, follow' } : {},
        ],
        link: links,
        script: scripts,
    }
})
useHead(pageMeta)
</script>
